import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import {
  Mail, Lock, AlertCircle, UserCircle, Handshake, Eye, EyeOff,
  Briefcase, ShieldCheck, BarChart3, FileCheck,
} from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password, rememberMe);
    if (result.success) {
      // Clients have a separate portal session — bounce them to /client-portal/login.
      if (result.role === 'client') {
        logout();
        navigate('/client-portal/login', {
          state: { notice: 'Please sign in via the Client Portal.' },
        });
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#00395d] via-[#004a7a] to-[#00395d] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background accents */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#00aeef] rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#00c9ff] rounded-full mix-blend-screen filter blur-3xl opacity-15 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#00395d] rounded-full mix-blend-multiply filter blur-3xl opacity-30"></div>
      </div>

      {/* Grid overlay */}
      <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:40px_40px]"></div>

      {/* Loading Bar */}
      {loading && (
        <div className="fixed top-0 left-0 right-0 z-50">
          <div className="h-1 bg-slate-700 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#00aeef] via-[#00c9ff] to-[#D4AF37] animate-loading-bar shadow-lg"></div>
          </div>
        </div>
      )}

      <div className="relative z-10 w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden shadow-2xl border border-[#00aeef]/20">
        {/* Left panel — Branded hero */}
        <div className="hidden lg:flex flex-col justify-between p-10 bg-gradient-to-br from-[#00395d] via-[#003d7a] to-[#00395d] relative">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(0,174,239,0.25),transparent_50%)]"></div>
          <div className="relative z-10">
            <img
              src="/company-logo.png"
              alt="Credys Financial Services"
              className="h-20 w-auto object-contain mb-8"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00aeef]/15 border border-[#00aeef]/30 mb-6">
              <Briefcase className="h-3.5 w-3.5 text-[#00c9ff]" />
              <span className="text-[11px] font-semibold text-[#00c9ff] uppercase tracking-wider">Admin / Broker Portal</span>
            </div>
            <h1 className="text-4xl font-bold text-white leading-tight mb-3">
              Manage your<br />mortgage practice
            </h1>
            <p className="text-slate-300 text-sm leading-relaxed max-w-sm">
              Sign in to access client records, applications, calendar and commissions in one Premier-styled workspace.
            </p>
          </div>

          {/* Feature tiles */}
          <div className="relative z-10 grid grid-cols-3 gap-3 mt-10">
            <div className="p-3 rounded-xl bg-white/5 backdrop-blur border border-white/10">
              <BarChart3 className="h-4 w-4 text-[#00c9ff] mb-2" />
              <p className="text-[11px] font-semibold text-white">Pipeline</p>
              <p className="text-[10px] text-slate-400">Live application kanban</p>
            </div>
            <div className="p-3 rounded-xl bg-white/5 backdrop-blur border border-white/10">
              <ShieldCheck className="h-4 w-4 text-[#00c9ff] mb-2" />
              <p className="text-[11px] font-semibold text-white">Compliance</p>
              <p className="text-[10px] text-slate-400">Audit-ready trails</p>
            </div>
            <div className="p-3 rounded-xl bg-white/5 backdrop-blur border border-white/10">
              <FileCheck className="h-4 w-4 text-[#00c9ff] mb-2" />
              <p className="text-[11px] font-semibold text-white">Reporting</p>
              <p className="text-[10px] text-slate-400">Daily & monthly KPIs</p>
            </div>
          </div>
        </div>

        {/* Right panel — Login form */}
        <Card className="border-0 rounded-none bg-slate-900/95 backdrop-blur-xl">
          <CardContent className="p-8 lg:p-10">
            {/* Mobile branded header */}
            <div className="lg:hidden text-center mb-6">
              <img
                src="/company-logo.png"
                alt="Credys"
                className="h-14 w-auto object-contain mx-auto mb-2"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#00aeef]/15 border border-[#00aeef]/30">
                <Briefcase className="h-3 w-3 text-[#00c9ff]" />
                <span className="text-[10px] font-semibold text-[#00c9ff] uppercase tracking-wider">Admin Portal</span>
              </div>
            </div>

            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white mb-1">Welcome back</h2>
              <p className="text-slate-400 text-sm">Sign in to your Admin / Broker dashboard</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-400">{error}</p>
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-300">Email / Username</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    data-testid="login-email-input"
                    type="email"
                    placeholder="your.email@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10 h-11 text-sm border border-slate-700 bg-slate-800/60 text-white placeholder:text-slate-500 focus:border-[#00aeef] focus:ring-2 focus:ring-[#00aeef]/20 rounded-lg"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <label className="text-xs font-medium text-slate-300">Password</label>
                  <a href="/forgot-password" className="text-xs text-[#00c9ff] hover:text-[#00aeef] font-medium">Forgot password?</a>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    data-testid="login-password-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pl-10 pr-10 h-11 text-sm border border-slate-700 bg-slate-800/60 text-white placeholder:text-slate-500 focus:border-[#00aeef] focus:ring-2 focus:ring-[#00aeef]/20 rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="remember-me"
                  data-testid="login-remember-me"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-700 bg-slate-800/60 text-[#00aeef] focus:ring-[#00aeef]/20 focus:ring-offset-0 cursor-pointer"
                />
                <label
                  htmlFor="remember-me"
                  className="text-xs text-slate-400 cursor-pointer select-none hover:text-slate-300"
                >
                  Remember me for 30 days
                </label>
              </div>

              <Button
                data-testid="login-submit-button"
                type="submit"
                className="w-full h-11 bg-gradient-to-r from-[#00aeef] to-[#00c9ff] hover:from-[#0098d4] hover:to-[#00aeef] text-white text-sm font-semibold shadow-lg shadow-[#00aeef]/30 rounded-lg mt-2"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    <span>Signing in...</span>
                  </div>
                ) : (
                  'Sign In to Admin Portal'
                )}
              </Button>

              <p className="text-center text-xs text-slate-500 pt-1">
                Forgot password?{' '}
                <Link to="/forgot-password" className="text-[#00c9ff] hover:text-[#00aeef] font-medium" data-testid="recover-access-link">
                  Recover access
                </Link>
              </p>
            </form>

            {/* Other portals */}
            <div className="mt-6 pt-5 border-t border-slate-800">
              <p className="text-slate-500 text-[11px] text-center mb-3 uppercase tracking-wider">Other portals</p>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/introducer-login')}
                  className="h-9 bg-slate-800/40 hover:bg-[#00aeef]/20 text-slate-300 hover:text-white border-slate-700 hover:border-[#00aeef] text-xs flex items-center justify-center gap-1.5"
                  data-testid="go-to-introducer-login"
                >
                  <Handshake className="h-3.5 w-3.5" />
                  Introducer
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/client-portal/login')}
                  className="h-9 bg-slate-800/40 hover:bg-[#00aeef]/20 text-slate-300 hover:text-white border-slate-700 hover:border-[#00aeef] text-xs flex items-center justify-center gap-1.5"
                  data-testid="go-to-client-login"
                >
                  <UserCircle className="h-3.5 w-3.5" />
                  Client
                </Button>
              </div>
            </div>

            <p className="text-[11px] text-slate-600 text-center mt-5">🔒 Bank-level security · FCA authorised</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
